import { gaTracking, getCurrentPageLocation } from "../../lib/ga";
import type { GAClickLink, GALinkType } from "../../types/ga";

// Re-export types for backward compatibility
export type { GAClickLink, GALinkType };

/**
 * Tracks link clicks for analytics purposes.
 *
 * @param url - The URL of the link being clicked
 * @param linkText - The text of the link being clicked
 * @param optionalParams - Additional optional parameters for tracking, such as section title, block index, link type, and insurance
 * @param clickElement - Optional clicked anchor element used to infer context when tracking data is missing
 *
 * @description This function sends a tracking event to Google Analytics when a link is clicked.
 * It includes details about the link and its context for better analysis.
 *
 * @example
 * linkTracker("https://example.com", "Example Link", {
 *   sectionTitle: "Header",
 *   sectionBlockIndex: 1,
 *   linkType: "click"
 * });
 *
 * @see /GAExamples/link_click.js for event structure
 */
const inferSectionTitleFromElement = (
  clickElement?: HTMLElement | null,
  linkText?: string,
): string | undefined => {
  if (!clickElement) {
    return undefined;
  }

  const normalizedLinkText = normalizeComparableText(linkText);

  const closestHeaderContainer = clickElement.closest("[data-header]");
  const dataHeader = closestHeaderContainer?.getAttribute("data-header")?.trim();

  if (dataHeader) {
    return dataHeader;
  }

  // Walk up the DOM tree and use the first nearby ancestor that contains a heading.
  // Skip headings identical to link text so title reflects section context instead of clicked item text.
  let current: HTMLElement | null = clickElement;
  let firstHeadingMatch: string | undefined;
  while (current && current !== document.body) {
    const headingInCurrent = current.querySelector("h1, h2, h3, h4, h5, h6")?.textContent?.trim();
    if (headingInCurrent) {
      const normalizedHeading = normalizeComparableText(headingInCurrent);
      if (!firstHeadingMatch) {
        firstHeadingMatch = headingInCurrent;
      }
      if (!normalizedLinkText || normalizedHeading !== normalizedLinkText) {
        return headingInCurrent;
      }
    }
    current = current.parentElement;
  }

  return firstHeadingMatch;
};

const normalizeComparableText = (value?: string): string =>
  (value || "").toLocaleLowerCase("sv-SE").trim();

const stripInvisibleCharacters = (value?: string): string | undefined => {
  if (!value) {
    return value ?? undefined;
  }

  const cleaned = value.replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, "").trim();
  return cleaned || undefined;
};

const inferSectionIndexFromElement = (clickElement?: HTMLElement | null): number | undefined => {
  if (!clickElement || typeof document === "undefined") {
    return undefined;
  }

  const contextElement = clickElement.closest("[data-uniquekey], [data-header]");
  const uniqueKey = contextElement?.getAttribute("data-uniquekey")?.trim();

  if (uniqueKey) {
    let end = uniqueKey.length - 1;
    while (end >= 0 && uniqueKey.charCodeAt(end) >= 48 && uniqueKey.charCodeAt(end) <= 57) {
      end -= 1;
    }

    const trailingNumber = end < uniqueKey.length - 1 ? uniqueKey.slice(end + 1) : undefined;
    if (trailingNumber) {
      const parsed = Number(trailingNumber);
      if (Number.isFinite(parsed)) {
        return parsed + 1;
      }
    }
  }

  const headerElement = clickElement.closest("[data-header]");
  if (headerElement) {
    const allHeaders = Array.from(document.querySelectorAll("[data-header]"));
    const headerIndex = allHeaders.indexOf(headerElement);

    if (headerIndex >= 0) {
      return headerIndex + 1;
    }
  }

  return undefined;
};

const normalizeInsuranceValue = (value?: string): string | undefined => {
  const normalized = (value || "").toLocaleLowerCase("sv-SE").trim();
  if (!normalized) {
    return undefined;
  }

  if (normalized.includes(" - ")) {
    return normalized;
  }

  const insuranceMap: Record<string, string> = {
    sjukforsakring: "ags - sjukförsäkring",
    sjukförsäkring: "ags - sjukförsäkring",
  };

  return insuranceMap[normalized] || normalized;
};

const getPathSegment = (path: string, index: number): string | undefined => {
  const pathSegments = path.split("/").filter(Boolean);
  if (pathSegments.length <= index) {
    return undefined;
  }

  return pathSegments[index] || undefined;
};

const OVERVIEW_PATH_SEGMENTS = new Set(["oversikt-forsakring", "anmalan-oversikt"]);

const getInferredInsuranceFromPath = (path: string): string | undefined => {
  const isInsuranceClaimPath = path.startsWith("/forsakring/") && path.includes("/anmal");
  if (!isInsuranceClaimPath) {
    return undefined;
  }

  const segment = getPathSegment(path, 1);
  return segment && OVERVIEW_PATH_SEGMENTS.has(segment) ? undefined : segment;
};

const getInferredInsuranceFromPagePath = (path: string): string | undefined => {
  if (!path.startsWith("/forsakring/")) {
    return undefined;
  }

  const segment = getPathSegment(path, 1);
  return segment && OVERVIEW_PATH_SEGMENTS.has(segment) ? undefined : segment;
};

const resolveSectionContext = (
  sectionTitle: string | undefined,
  sectionBlockIndex: number | undefined,
  effectiveLinkType: GALinkType,
  linkText: string,
  clickElement?: HTMLElement | null,
): { title: string | undefined; index: number | undefined } => {
  const fallbackSectionTitle = inferSectionTitleFromElement(clickElement, linkText);
  const fallbackSectionIndex = inferSectionIndexFromElement(clickElement);
  const pageHeadingTitle = document?.querySelector("main h1, h1")?.textContent?.trim() || undefined;
  const fallbackPageTitle = document?.title?.trim() || undefined;
  const sectionTitleNormalized = normalizeComparableText(sectionTitle);
  const inferredTitleNormalized = normalizeComparableText(fallbackSectionTitle);
  const pageHeadingNormalized = normalizeComparableText(pageHeadingTitle);
  const normalizedLinkText = (linkText || "").toLowerCase().trim();
  const isNewsLink = effectiveLinkType === "news";

  const shouldPreferInferredTitle =
    !!fallbackSectionTitle &&
    (!sectionTitle ||
      (isNewsLink &&
        inferredTitleNormalized !== "" &&
        inferredTitleNormalized !== normalizedLinkText) ||
      (pageHeadingNormalized !== "" &&
        sectionTitleNormalized === pageHeadingNormalized &&
        inferredTitleNormalized !== pageHeadingNormalized));

  const title =
    shouldPreferInferredTitle || !sectionTitle
      ? fallbackSectionTitle || fallbackPageTitle
      : sectionTitle;

  const hasExplicitSectionBlockIndex =
    sectionBlockIndex !== undefined && sectionBlockIndex !== null;
  const normalizedSectionBlockIndex =
    typeof sectionBlockIndex === "number" && sectionBlockIndex <= 0
      ? fallbackSectionIndex || 1
      : sectionBlockIndex;
  const index = hasExplicitSectionBlockIndex ? normalizedSectionBlockIndex : fallbackSectionIndex;

  return {
    title,
    index: effectiveLinkType === "footer" ? undefined : index,
  };
};

const toAbsoluteUrl = (url: string): string => {
  if (!url || url.startsWith("http://") || url.startsWith("https://") || url.includes(":")) {
    return url;
  }

  const origin = window.location.origin;
  return url.startsWith("/") ? `${origin}${url}` : `${origin}/${url}`;
};

export const linkTracker = (
  url: string,
  linkText: string,
  optionalParams?: GAClickLink,
  clickElement?: HTMLElement | null,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  const { sectionTitle, sectionBlockIndex, linkType = "click", insurance } = optionalParams ?? {};

  const currentPath = window.location.pathname || "";
  const normalizedLinkText = (linkText || "").toLowerCase().trim();
  const inferredBarkerFromContext = normalizedLinkText.includes("gå vidare till anmälan");

  const effectiveLinkType: GALinkType =
    linkType === "click" && inferredBarkerFromContext ? "barker" : linkType;
  const inferredInsuranceFromPath = getInferredInsuranceFromPath(currentPath);
  const inferredInsuranceFromPagePath = getInferredInsuranceFromPagePath(currentPath);

  // Only barker and shortcut links carry an insurance value (see DigitalAnalys wiki 29298).
  const effectiveInsurance =
    effectiveLinkType === "barker"
      ? insurance || inferredInsuranceFromPath || inferredInsuranceFromPagePath
      : insurance;
  const sectionContext = resolveSectionContext(
    sectionTitle,
    sectionBlockIndex,
    effectiveLinkType,
    linkText,
    clickElement,
  );
  const cleanInsurance = normalizeInsuranceValue(effectiveInsurance || undefined);
  const absoluteUrl = toAbsoluteUrl(url);

  gaTracking({
    event: "link_click",
    event_parameters: {
      page_location: getCurrentPageLocation(),
      title: stripInvisibleCharacters(sectionContext.title),
      index: sectionContext.index,
      link_text: stripInvisibleCharacters(linkText) ?? linkText,
      link_url: absoluteUrl,
      link_type: effectiveLinkType,
      insurance: cleanInsurance,
    },
  });
};
